'use client';

import clsx from 'clsx';
import * as React from 'react';

const STAR_PATH =
  'M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z';
const STAR_BORDER_PATH =
  'M22 9.24l-7.19-.62L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24zM12 15.4l-3.76 2.27 1-4.28-3.32-2.88 4.38-.38L12 6.1l1.71 4.04 4.38.38-3.32 2.88 1 4.28L12 15.4z';

const sizeClasses = {
  small: 'size-[18px]',
  medium: 'size-6',
  large: 'size-[30px]'
} as const;

export type RatingSize = keyof typeof sizeClasses;

const FILLED_COLOR_CLASS = 'text-[#faaf00]';
const EMPTY_COLOR_CLASS = 'text-(--mui-palette-action-disabled)';

function getDecimalPrecision(num: number) {
  const decimalPart = num.toString().split('.')[1];
  return decimalPart ? decimalPart.length : 0;
}

function roundValueToPrecision(value: number, precision: number) {
  const nearest = Math.round(value / precision) * precision;
  return Number(nearest.toFixed(getDecimalPrecision(precision)));
}

function defaultGetLabelText(value: number) {
  return `${value || '0'} Star${value !== 1 ? 's' : ''}`;
}

interface RatingIconProps extends React.SVGProps<SVGSVGElement> {
  path: string;
}

function RatingIcon({ path, className, ...rest }: RatingIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      focusable="false"
      aria-hidden="true"
      className={clsx('block', className)}
      {...rest}
    >
      <path d={path} fill="currentColor" />
    </svg>
  );
}

function RatingStarDisplay({ fraction, size }: { fraction: number; size: RatingSize }) {
  if (fraction <= 0) {
    return (
      <RatingIcon
        path={STAR_BORDER_PATH}
        data-slot="icon-empty"
        className={clsx(sizeClasses[size], EMPTY_COLOR_CLASS)}
      />
    );
  }

  if (fraction >= 1) {
    return (
      <RatingIcon
        path={STAR_PATH}
        data-slot="icon-filled"
        className={clsx(sizeClasses[size], FILLED_COLOR_CLASS)}
      />
    );
  }

  return (
    <span className={clsx('relative inline-block', sizeClasses[size])}>
      <RatingIcon
        path={STAR_BORDER_PATH}
        data-slot="icon-empty"
        className={clsx('absolute inset-0 size-full', EMPTY_COLOR_CLASS)}
      />
      <span className="absolute inset-0 overflow-hidden" style={{ width: `${fraction * 100}%` }}>
        <RatingIcon
          path={STAR_PATH}
          data-slot="icon-filled"
          className={clsx(sizeClasses[size], FILLED_COLOR_CLASS)}
        />
      </span>
    </span>
  );
}

export interface RatingProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'onChange' | 'defaultValue'> {
  value?: number | null;
  defaultValue?: number | null;
  onChange?: (event: React.SyntheticEvent, value: number | null) => void;
  max?: number;
  precision?: number;
  readOnly?: boolean;
  disabled?: boolean;
  size?: RatingSize;
  name?: string;
  getLabelText?: (value: number) => string;
}

export const Rating = React.forwardRef<HTMLDivElement, RatingProps>(function Rating(
  {
    value: valueProp,
    defaultValue = null,
    onChange,
    max = 5,
    precision = 1,
    readOnly = false,
    disabled = false,
    size = 'medium',
    name: nameProp,
    getLabelText = defaultGetLabelText,
    className,
    'aria-label': ariaLabelProp,
    ...rest
  },
  ref
) {
  const isControlled = valueProp !== undefined;
  const [valueState, setValueState] = React.useState<number | null>(defaultValue);
  const value = isControlled ? valueProp : valueState;

  const [hoverValue, setHoverValue] = React.useState<number | null>(null);

  const generatedName = React.useId();
  const name = nameProp ?? generatedName;

  const roundedValue = roundValueToPrecision(value ?? 0, precision);
  const displayValue = hoverValue ?? roundedValue;

  const stars = Array.from({ length: max }, (_, index) => index + 1);

  if (readOnly) {
    const label = ariaLabelProp ?? getLabelText(roundedValue);
    return (
      <div
        ref={ref}
        role="img"
        aria-label={label}
        className={clsx('inline-flex', disabled && 'opacity-[0.38] pointer-events-none', className)}
        {...rest}
      >
        {stars.map((starValue) => {
          const fraction = Math.min(Math.max(roundedValue - (starValue - 1), 0), 1);
          return <RatingStarDisplay key={starValue} fraction={fraction} size={size} />;
        })}
      </div>
    );
  }

  const handleSelect = (event: React.ChangeEvent<HTMLInputElement>, itemValue: number) => {
    if (disabled) {
      return;
    }
    if (!isControlled) {
      setValueState(itemValue);
    }
    onChange?.(event, itemValue);
  };

  return (
    <div
      ref={ref}
      role="radiogroup"
      aria-label={ariaLabelProp}
      className={clsx('inline-flex', disabled && 'opacity-[0.38] pointer-events-none', className)}
      onMouseLeave={() => setHoverValue(null)}
      {...rest}
    >
      {stars.map((starValue) => {
        const isFilled = starValue <= displayValue;
        return (
          <label
            key={starValue}
            className="cursor-pointer"
            onMouseEnter={() => !disabled && setHoverValue(starValue)}
          >
            <input
              type="radio"
              name={name}
              value={starValue}
              checked={roundedValue === starValue}
              disabled={disabled}
              onChange={(event) => handleSelect(event, starValue)}
              className="sr-only"
            />
            <span className="sr-only">{getLabelText(starValue)}</span>
            <RatingIcon
              path={isFilled ? STAR_PATH : STAR_BORDER_PATH}
              data-slot={isFilled ? 'icon-filled' : 'icon-empty'}
              className={clsx(sizeClasses[size], isFilled ? FILLED_COLOR_CLASS : EMPTY_COLOR_CLASS)}
            />
          </label>
        );
      })}
    </div>
  );
});
